import React, { useEffect, useState } from 'react';
import Modal from '../../../../templates/contents/Modal';
import Container from '@material-ui/core/Container';
import Alert from '@material-ui/lab/Alert';
import CircularProgress from '@material-ui/core/CircularProgress';
import DataProvider from '../../../../provider';
import { useHistory, useLocation } from 'react-router-dom';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { CrossStoreType, setKey } from '../../../../cross-store';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { pushTrackingEvent } from '../../../../utils/gtmDataLayer';
import dropin from 'braintree-web-drop-in';

const DROPIN_ID = 'google-pay-dropin-button';


const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4, 0),
    height: '100%',
    width: '100%'
  },
  button: {
    width: '70%',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonPlaceholder: {
    width: '70%'
  },
  googlePay: {
    '& .braintree-placeholder':{
      display: 'none'
    },
    '& .braintree-sheet__content': {
      padding: 0
    },
    '& #gpay-button-online-api-id': {
      width: '200px'
    },
    '& [data-braintree-id="google-pay-sheet-header"]': {
      display: "none"
    },
    '& [data-braintree-id="googlePay"]': {
      padding: 0,
      margin: 0,
      border: 'none',
      background: 'transparent',
    },
  },
  text: {
    margin: theme.spacing(0, 0, 4),
  },
  errorButton: {
    margin: theme.spacing(0, 0, 1),
  },
  errorButtonContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '70%',
    margin: theme.spacing(4, 0, 0),
  }
}));

export default (props) => {
  const history = useHistory();
  const classes = useStyles();

  const [nonce, setNonce] = useState<string>();
  const [clientToken, setClientToken] = useState<any | null>(null);
  const [status, setStatus] = useState(
    'INITIAL' as
      | 'INITIAL'
      | 'FETCH_TOKEN'
      | 'FETCHING_TOKEN'
      | 'ERROR_FETCHING_TOKEN'
      | 'CREATE_CLIENT'
      | 'CREATING_CLIENT'
      | 'ERROR_CREATING_CLIENT'
      | 'CLIENT_CREATED'
      | 'SEND_NONCE'
      | 'SENDING_NONCE'
      | 'ERROR_SENDING_NONCE'
      | 'ERROR_SUPPORTED_DEVICE'
  );

  const [errorSendingNonce, setErrorSendingNonce] = useState<string>("C'è stato un errore. Riprova o contattaci al +390289763305 (Gateway error: 20142)");

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const return_url = searchParams.get('return-url');
  const key_to_update: CrossStoreType = searchParams.get('key-to-update') as CrossStoreType;

  useEffect(() => {
    if (status === 'INITIAL') {
      setStatus('FETCH_TOKEN');
    }

    if (status === 'FETCH_TOKEN') {
      setStatus('FETCHING_TOKEN');
      const dataProvider = DataProvider(process.env.REACT_APP_WAREHOUSE_API || '');
      dataProvider('b2c/payment/integration/braintree/token')
        .then((clientToken) => {
          setClientToken(clientToken);
          setStatus('CREATE_CLIENT');
        })
        .catch((e) => {
          e &&
            pushTrackingEvent({
              type: 'TrackingEvent',
              data: {
                category: 'error-google-pay-add',
                action: 'getting_token',
                label: JSON.stringify(e)
              }
            });
          setStatus('ERROR_FETCHING_TOKEN');
        });
    }

    if (status === 'CREATE_CLIENT' && clientToken) {
      setStatus('CREATING_CLIENT');

      dropin.create({
        authorization: clientToken.clientToken,
        container: `#${DROPIN_ID}`,
        locale: 'it_IT',
        card: false,
        vaultManager: false,
        googlePay: {
          googlePayVersion: 2,
          merchantId: 'BCR2DN5T3CN7TZIZ',
          transactionInfo: {
            totalPriceStatus: "FINAL",
            currencyCode: "EUR",
            totalPrice: "0.00"
          },
          allowedPaymentMethods: [{
            type: 'CARD',
            parameters: {
              billingAddressRequired: false,
            }
          }]
        }
      }, (error, instance) => {
        if(error) {
          pushTrackingEvent({
            type: 'TrackingEvent',
            data: {
              category: 'error-google-pay-add',
              action: 'creating_client',
              label: JSON.stringify(error)
            }
          });
          setStatus('ERROR_CREATING_CLIENT');
          return;
        }

        setStatus('CLIENT_CREATED');

        instance.on('paymentMethodRequestable', () => {
          instance.requestPaymentMethod((error, payload) => {
            if (error) {
              pushTrackingEvent({
                type: 'TrackingEvent',
                data: {
                  category: 'error-google-pay-add',
                  action: 'parsing_payment',
                  label: JSON.stringify(error)
                }
              });
              return
            }

            setNonce(payload.nonce);
            setStatus('SEND_NONCE');
          })
        })
      })
    }

    if (status === 'SEND_NONCE' && nonce) {
      setStatus('SENDING_NONCE');
      const dataProvider = DataProvider(process.env.REACT_APP_WAREHOUSE_API || '');
      dataProvider('b2c/payment/integration/braintree/braintree-googlepay-pi/add-account', {
        method: 'POST',
        body: JSON.stringify({ nonce })
      })
        .then((paymentInstrument) => {
          if (history.location.state && history.location.state['key_to_update']) {
            setKey(history.location.state['key_to_update'], paymentInstrument);
          } else if (key_to_update) {
            setKey(key_to_update, paymentInstrument);
            if (return_url) {
              history.replace(decodeURIComponent(return_url));
              return;
            }
          }

          history.goBack();
        })
        .catch((e) => {
          e &&
            pushTrackingEvent({
              type: 'TrackingEvent',
              data: {
                category: 'error-google-pay-add',
                action: 'sending_nonce',
                label: JSON.stringify(e)
              }
            });
          if (e && e.message) {
            setErrorSendingNonce(e.message);
          }
          setStatus('ERROR_SENDING_NONCE');
        });
    }
  }, [status, clientToken, nonce, history, key_to_update, return_url]);

  useEffect(() => {
    const isSafari = () => {
      const ua = navigator.userAgent;
      const isWebKit = /AppleWebKit/.test(ua);
      const isChrome = /Chrome|CriOS/.test(ua);
      const isFirefox = /FxiOS/.test(ua);
  
      // @ts-ignore
      return isWebKit && !isChrome && !isFirefox && !!window.ApplePaySession;
    };
  
    if(isSafari()) {
      setStatus('ERROR_SUPPORTED_DEVICE');
    }
  },[])



  return (
    <Modal title="Collega Google Pay" action={history.goBack}>
      <Container className={classes.container} component="main" maxWidth="xs">
        {['ERROR_SENDING_NONCE'].includes(status) && <Alert severity="error">{errorSendingNonce}</Alert>}

        {['ERROR_FETCHING_TOKEN', 'ERROR_CREATING_CLIENT'].includes(status) && (
          <Alert severity="error">
            C'è stato un errore. Riprova o contattaci al +390289763305 (Gateway error: 20144)
          </Alert>
        )}

        {['ERROR_SUPPORTED_DEVICE'].includes(status) && (
          <>
            <Alert severity="error">
              Questo browser non supporta Google Pay.
            </Alert>
            <div className={classes.errorButtonContainer}>
              <Button variant="outlined" onClick={history.goBack}>
                indietro
              </Button>
            </div>
          </>
        )}

        {['ERROR_FETCHING_TOKEN', 'ERROR_CREATING_CLIENT', 'ERROR_SENDING_NONCE'].includes(status) && (
          <div className={classes.errorButtonContainer}>
            <Button
              color="primary"
              className={classes.errorButton}
              onClick={() => {
                setStatus('INITIAL');
              }}
              variant="contained"
              size="large"
            >
              Riprova
            </Button>
            <Button variant="outlined" onClick={history.goBack}>
              indietro
            </Button>
          </div>
        )}

        {['INITIAL', 'FETCH_TOKEN', 'FETCHING_TOKEN', 'CREATE_CLIENT', 'CREATING_CLIENT', 'CLIENT_CREATED', 'SENDING_NONCE'].includes(status) && (
          <Typography align="center" className={classes.text}>
            Clicca qui per aggiungere Google Pay al tuo profilo Foorban.
          </Typography>
        )}

        <div
          style={{ display: status === 'CLIENT_CREATED' ? 'flex' : 'none'}}
          className={`${classes.button} ${classes.googlePay}`}
          id={DROPIN_ID}
        ></div>

        {['INITIAL', 'FETCH_TOKEN', 'FETCHING_TOKEN', 'CREATE_CLIENT', 'CREATING_CLIENT', 'SENDING_NONCE'].includes(status) && (
          <Button className={classes.buttonPlaceholder} variant="contained" size="large">
            <CircularProgress style={{ width: '28px', height: '28px' }} color="inherit" />
          </Button>
        )}
      </Container>
    </Modal>
  );
};
